import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import { useLangStore } from "../store/lang";
import { t } from "../i18n/strings";
import { LanguagePicker } from "../components/LanguagePicker";
import { api, ApiError } from "../lib/api";

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; name: string };
  tenant: { id: string; role: "admin" | "user" | "read_only" } | null;
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const setSession = useAuthStore((s) => s.setSession);
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/dashboard";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<LoginResponse>("/auth/login", { email, password });
      setSession({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        user: res.user,
        tenant: res.tenant ? { id: res.tenant.id, role: res.tenant.role } : null,
      });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid email or password.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div>
        <div className="auth-card">
          <div className="auth-brand">
            <div className="brand-mark">C</div>
            <span className="name">ClearBooks</span>
          </div>
          <h1 className="auth-headline">
            Welcome <em>back.</em>
          </h1>
          <p className="auth-sub">{t("auth.signIn", lang)} to continue to your books.</p>

          <form onSubmit={onSubmit} className="auth-fields">
            <div className="form-field">
              <label className="form-label">{t("auth.email", lang)}</label>
              <input
                className="form-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label className="form-label">{t("auth.password", lang)}</label>
              <input
                className="form-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="banner banner-error">{error}</div>}
            <div className="auth-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting} style={{ justifyContent: "center" }}>
                {submitting ? t("common.loading", lang) : t("auth.signIn", lang)}
              </button>
            </div>
          </form>

          <div className="auth-foot">
            {t("auth.noAccount", lang)} <Link to="/register">{t("auth.signUp", lang)}</Link>
          </div>
        </div>
        <div className="auth-lang">
          <LanguagePicker />
        </div>
      </div>
    </div>
  );
}
